import { useEffect, useMemo, useState } from 'react';
import type { CopilotTool } from '@/types/copilot';

const TOOL_SELECTION_STORAGE_KEY = 'mcp_copilot_selected_tool_ids_v1';

const readSelectionFromStorage = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.sessionStorage.getItem(TOOL_SELECTION_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === 'string');
    }
  } catch (error) {
    console.error('Failed to read tool selection from session storage:', error);
  }

  return [];
};

const persistSelectionToStorage = (selection: string[]) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(TOOL_SELECTION_STORAGE_KEY, JSON.stringify(selection));
  } catch (error) {
    console.error('Failed to persist tool selection to session storage:', error);
  }
};

export interface ToolSelectionState {
  selectedToolIds: string[];
  isAllSelected: boolean;
  toggleTool: (toolId: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  setSelection: (next: string[]) => void;
}

export const useToolSelection = (allowedTools: CopilotTool[] | null | undefined): ToolSelectionState => {
  const toolIds = useMemo(
    () => (allowedTools ?? []).map(tool => tool.id).filter((id): id is string => typeof id === 'string' && id.length > 0),
    [allowedTools]
  );

  const [selectedToolIds, setSelectedToolIds] = useState<string[]>(() => {
    if (toolIds.length === 0) {
      return [];
    }

    const stored = readSelectionFromStorage();
    const validStored = stored.filter(id => toolIds.includes(id));

    if (validStored.length > 0) {
      return Array.from(new Set(validStored));
    }

    return toolIds;
  });

  // Ensure selection is aligned with available tools
  useEffect(() => {
    if (toolIds.length === 0) {
      setSelectedToolIds([]);
      return;
    }

    setSelectedToolIds(prev => {
      const filteredPrev = prev.filter(id => toolIds.includes(id));
      if (filteredPrev.length === prev.length && filteredPrev.length > 0) {
        return filteredPrev;
      }

      const stored = readSelectionFromStorage();
      const validStored = stored.filter(id => toolIds.includes(id));
      if (validStored.length > 0) {
        return Array.from(new Set(validStored));
      }

      return toolIds;
    });
  }, [toolIds]);

  // Persist selection
  useEffect(() => {
    persistSelectionToStorage(selectedToolIds);
  }, [selectedToolIds]);

  const toggleTool = (toolId: string) => {
    setSelectedToolIds(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId);
      }
      return [...prev, toolId];
    });
  };

  const selectAll = () => {
    setSelectedToolIds(toolIds);
  };

  const deselectAll = () => {
    setSelectedToolIds([]);
  };

  const setSelection = (next: string[]) => {
    const unique = Array.from(new Set(next.filter(id => toolIds.includes(id))));
    setSelectedToolIds(unique);
  };

  const isAllSelected = selectedToolIds.length > 0 && selectedToolIds.length === toolIds.length;

  return {
    selectedToolIds,
    isAllSelected,
    toggleTool,
    selectAll,
    deselectAll,
    setSelection,
  };
};
